import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import apiService from 'app/bff/services/apiService';
import { SystemParamTypesEnum, SystemParamIdEnum } from 'app/bff/enums/system';
import commonService from 'app/core/services/commonService';
import storageService from 'app/core/services/storageService';
import { ModalNamesEnum } from 'app/core/enums/ui/modals';
import { StorageKeysEnum } from 'app/core/enums/storage';
import { setModalVisibleAction } from 'app/store/ui/actions';
import Modal from 'app/common/components/Modals/Modal';
import { IdleModalProps } from './types';
import { LoginTypesEnum } from 'app/bff/enums/login';
import { RootState } from 'app/store/types';
import { resetMemberCacheAction } from 'app/store/common/actions';

const IdleModal: React.FC<IdleModalProps> = (props) => {
  const reduxDispatch = useDispatch();
  const memberState = useSelector((state: RootState) => state.common.member);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const [countdownSecondsState, setCountdownSecondsState] = useState<number>(0);

  /**
   * @description Component unmount cleanup
   */
  useEffect(() => {
    return () => {
      // 停止計時器
      cleanupInterval();
    };
  }, []);

  /**
   * @description 執行倒數計時
   */
  const executeCountdown = useCallback((initialSeconds: number) => {
    let secondsRemaining = initialSeconds;
    // 同步初始的剩餘秒數至畫面
    setCountdownSecondsState(secondsRemaining);
    // 計時器
    countdownIntervalRef.current = setInterval(() => {
      if (secondsRemaining > 0) {
        // 每秒遞減
        setCountdownSecondsState(secondsRemaining -= 1);
      } else {
        // 執行登出
        executeLogout();
        // 停止計時器
        cleanupInterval();
        // 關閉閒置跳窗
        reduxDispatch(setModalVisibleAction(ModalNamesEnum.IdleModal, false));
      }
    }, 1000);
  }, [reduxDispatch]);

  /**
   * @description 停止計時器
   */
  const cleanupInterval = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
  };

  /**
   * @description 取得閒置後倒數登出時間 (秒)
   */
  useEffect(() => {
    (async () => {
      const response = await apiService.postInfoSysparams({ paramType: SystemParamTypesEnum.CalculateTime, paramId: SystemParamIdEnum.Reciprocal });
      if (response && response.length > 0) {
        executeCountdown(+response[0].paramValue);
      }
    })();
  }, [executeCountdown]);

  /**
   * @description 處理登出
   */
  const executeLogout = async () => {
    apiService.postMbSignout({
      loginType: LoginTypesEnum.FrontendLogout,
      USER_ID: memberState.profile?.USERS.USER_ID ?? '',
      userName: memberState.profile?.USERS.userName ?? ''
    });
    // 重新生成 Session Id
    commonService.generateSessionId(true);
    // 清除「會員主檔」和「Access Token」
    storageService.removeItem(StorageKeysEnum.Authorization);
    reduxDispatch(resetMemberCacheAction());
    // 執行瀏覽器重新整理
    window.location.reload();
  };

  return (
    <Modal
      name={ModalNamesEnum.IdleModal}
      className="idle-modal"
      visible={props.visible}
    >
      <div className="position-relative">
        <img src={require('assets/img/pic/idle-modal-bear.svg').default} alt="" />
        <div className="dot dot0" />
        <div className="dot dot1" />
        <div className="dot dot2" />
      </div>
      <div className="d-flex align-items-center flex-column">
        <h5>閒置</h5>
        <p>我們發現您在此閒置過久，
          <br className="d-sm-none" />
          為保護您的資料安全，
          <br className="d-sm-none" />
          系統將在
          <span id="count-second">{countdownSecondsState}</span>
          秒後為您自動登出。
        </p>
      </div>
    </Modal>
  );
};

export default IdleModal;
